import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../lib/format';
import type { OrderInfo } from '../../stores/klanten-store';
import { useUserRole } from '../../hooks/useUserRole';

/**
 * Klantpagina-kaart "Terugkom-moment" (plan 2026-10-22): toont het moment dat
 * de klant na aflevering koos voor de volgende reiniging (pill), laat kantoor
 * er een zetten (telefoon) of wissen. Data via GET/POST/PATCH
 * /api/klanten/terugkom; de cron klant-terugkom maakt op de startdag de
 * herhaalofferte en mailt TK02. Mens in de lus, geen flag.
 */
interface TerugkomRij {
  id: string;
  bron_werk_order_sf_id: string;
  terugkom_vanaf: string;
  terugkom_tot: string;
  seizoen_label: string | null;
  reden_klant: string | null;
  kanaal_voorkeur: string | null;
  bron: string;
  status: string;
  uitkomst_reden: string | null;
  herhaalofferte_lead_sf_id: string | null;
  created_at: string;
  verwerkt_at: string | null;
}

interface Stand {
  open: TerugkomRij | null;
  laatste: TerugkomRij | null;
  opties: Array<{ label: string; vanaf: string; tot: string }>;
  order: { sfId: string; name: string; afgeleverd: boolean; afleverdatum: string } | null;
}

interface Props {
  accountId: string;
  orders: OrderInfo[];
}

const BRON_LABEL: Record<string, string> = {
  'client-app': 'klant, in de app',
  'mail-link': 'klant, via de mail',
  office: 'kantoor',
  voice: 'telefoon',
  lezer: 'mail-lezer',
};

const UITKOMST_LABEL: Record<string, string> = {
  tk02_verstuurd: 'herhaalofferte verstuurd',
  al_besteld: 'vervallen: klant had al besteld',
  open_offerte: 'vervallen: er stond al een offerte open',
  blacklist: 'vervallen: blacklist',
  vervangen: 'vervangen door een nieuwe keuze',
  klant: 'door de klant ingetrokken',
  geen_email: 'taak: geen e-mailadres',
  te_laat: 'taak: te laat opgepakt',
  prefill_mislukt: 'taak: order niet naar offerte te vertalen',
  offerte_mislukt: 'taak: offerte aanmaken mislukt',
  order_weg: 'vervallen: order weg',
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

function kapitaal(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function TerugkomMomentKaart({ accountId, orders }: Props) {
  const [stand, setStand] = useState<Stand | null>(null);
  const [laden, setLaden] = useState(true);
  const [zetten, setZetten] = useState(false);
  const [keuze, setKeuze] = useState('');
  const [reden, setReden] = useState('');
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const { role } = useUserRole();
  const kanMuteren = role === 'admin' || role === 'medewerker' || role === 'callcenter';

  const laad = useCallback(async () => {
    setLaden(true);
    setFout(null);
    try {
      const res = await fetch(`/api/klanten/terugkom?account=${encodeURIComponent(accountId)}`, { headers: await authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setStand({ open: json.open ?? null, laatste: json.laatste ?? null, opties: json.opties ?? [], order: json.order ?? null });
    } catch (err) {
      setFout(err instanceof Error ? err.message : String(err));
    } finally {
      setLaden(false);
    }
  }, [accountId]);

  useEffect(() => { void laad(); }, [laad]);

  const heeftAfgeleverd = orders.some((o) => o.status === 'Teruggebracht' || o.status === 'Afgerond') || stand?.order?.afgeleverd === true;
  if (!laden && !stand?.open && !stand?.laatste && !heeftAfgeleverd) return null;

  const zet = async () => {
    if (!stand?.order || !keuze) return;
    setBezig(true);
    setFout(null);
    try {
      const res = await fetch('/api/klanten/terugkom', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ wo: stand.order.sfId, account: accountId, seizoen: keuze, redenKlant: reden.trim() || null }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setZetten(false);
      setKeuze('');
      setReden('');
      await laad();
    } catch (err) {
      setFout(err instanceof Error ? err.message : String(err));
    } finally {
      setBezig(false);
    }
  };

  const wis = async () => {
    if (!stand?.open) return;
    if (!window.confirm('Terugkom-moment wissen? De klant krijgt dan geen herhaalofferte op dat moment.')) return;
    setBezig(true);
    setFout(null);
    try {
      const res = await fetch('/api/klanten/terugkom', {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ id: stand.open.id, account: accountId, reden: 'gewist door kantoor' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      await laad();
    } catch (err) {
      setFout(err instanceof Error ? err.message : String(err));
    } finally {
      setBezig(false);
    }
  };

  const open = stand?.open ?? null;
  const laatste = stand?.laatste ?? null;
  const orderNaam = (sfId: string) => orders.find((o) => o.id === sfId)?.reference ?? stand?.order?.name ?? sfId;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Terugkom-moment</h3>
          <p className="text-xs text-slate-500">
            Volgende reiniging zoals de klant hem koos; op de startdag gaat automatisch een herhaalofferte met de prijs van dan (TK02).
          </p>
        </div>
        {kanMuteren && !zetten && stand?.order?.afgeleverd && (
          <button type="button" onClick={() => setZetten(true)} className="text-xs font-medium text-sky-700 hover:underline whitespace-nowrap">
            {open ? 'Wijzigen' : 'Zetten'}
          </button>
        )}
      </div>

      {laden && <p className="mt-3 text-xs text-slate-400">Laden…</p>}
      {fout && <p className="mt-3 text-xs text-red-600">{fout}</p>}

      {!laden && open && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
            Terugkom: {open.seizoen_label ? kapitaal(open.seizoen_label) : formatDate(open.terugkom_vanaf)}
          </span>
          <span className="text-xs text-slate-500">
            vanaf {formatDate(open.terugkom_vanaf)} · order {orderNaam(open.bron_werk_order_sf_id)} · gezet {formatDate(open.created_at)} door {BRON_LABEL[open.bron] ?? open.bron}
            {open.reden_klant ? ` · "${open.reden_klant}"` : ''}
            {open.kanaal_voorkeur && open.kanaal_voorkeur !== 'mail' ? ` · via ${open.kanaal_voorkeur}` : ''}
          </span>
          {kanMuteren && (
            <button type="button" disabled={bezig} onClick={() => void wis()} className="text-xs text-slate-500 hover:text-red-600 hover:underline disabled:opacity-50">
              Wissen
            </button>
          )}
        </div>
      )}

      {!laden && !open && laatste && (
        <p className="mt-3 text-xs text-slate-500">
          Laatste: {laatste.seizoen_label ? kapitaal(laatste.seizoen_label) : formatDate(laatste.terugkom_vanaf)} ({UITKOMST_LABEL[laatste.uitkomst_reden ?? ''] ?? laatste.status}
          {laatste.verwerkt_at ? `, ${formatDate(laatste.verwerkt_at)}` : ''}).
        </p>
      )}

      {!laden && !open && !laatste && stand?.order?.afgeleverd && (
        <p className="mt-3 text-xs text-slate-400">Nog geen moment gekozen. De klant kan dat in de app; kantoor kan het hier zetten na een telefoontje.</p>
      )}

      {zetten && stand?.order && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
          <p className="text-xs text-slate-600">
            Op basis van order {stand.order.name} (afgeleverd {formatDate(stand.order.afleverdatum)}). Kies het seizoen dat de klant noemde.
          </p>
          <div className="flex flex-wrap gap-2">
            {stand.opties.map((o) => (
              <button
                key={o.label}
                type="button"
                onClick={() => setKeuze(o.label)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${keuze === o.label ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-500'}`}
              >
                {kapitaal(o.label)} <span className="font-normal opacity-70">(vanaf {formatDate(o.vanaf)})</span>
              </button>
            ))}
            {stand.opties.length === 0 && <span className="text-xs text-slate-500">Geen kiesbare seizoenen (de order is te oud of te vers).</span>}
          </div>
          <input
            type="text"
            value={reden}
            onChange={(e) => setReden(e.target.value)}
            maxLength={120}
            placeholder='In de woorden van de klant, bv. "na de zomervakantie" (komt in de mail)'
            className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
          />
          <div className="flex gap-2">
            <button type="button" disabled={!keuze || bezig} onClick={() => void zet()} className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
              Vastleggen
            </button>
            <button type="button" disabled={bezig} onClick={() => { setZetten(false); setKeuze(''); setFout(null); }} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs text-slate-700">
              Annuleren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
